import type { ISO2 } from '@/constants/countries';

/**
 * Types based on the GraphQL schema & PORTFOLIO_FRAGMENT (defined below)
 * See: /packages/tests/cypress/support/schema.graphql
 */
export interface Company {
  id: string;
  name: string;
  tickerSymbol: string;
  exchangeSymbol: string;
  uniqueSymbol: string;
  canonicalURL: string;
  exchangeCountryISO: ISO2;
  info: {
    url: string | null;
  } | null;
  data: {
    currencyInfo: unknown;
  };
  score: {
    value: number;
    future: number;
    past: number;
    health: number;
    dividend: number;
    sentence: string;
    total: number;
  } | null;
  analysisDividend: {
    dividendYield: number | null;
  } | null;
  analysisFuture: {
    forwardPe1Y: number | null;
    forwardPriceToSales1Y: number | null;
    revenueGrowthAnnual: number | null;
    netIncomeGrowthAnnual: number | null;
  } | null;
  analysisHealth: {
    netIncome: number | null;
  } | null;
  analysisValue: {
    pe: number | null;
    pb: number | null;
    priceToSales: number | null;
    preferredRelativeMultiple: 'pe' | 'ps' | 'pb' | string | null;
    lastSharePrice: number | null;
    npvPerShare: number | null;
    marketCapUSD: number | null;
    intrinsicDiscount: number | null;
    priceTarget: number | null;
    priceTargetAnalystCount: number | null;
  } | null;
  myValuation: {
    id: string;
    fairValue: number;
    type: string | null;
    narrative: {
      id: string;
      author: {
        id: string;
        displayName: string | null;
        name: string;
        imageUrl: string | null;
      };
    } | null;
  } | null;
}

type PortfolioLinkStatus =
  | 'manually_created'
  | 'created'
  | 'importing'
  | 'synced'
  | 'disconnected'
  | 'email_not_confirmed'
  | 'resyncing';

type PortfolioType = 'linked' | 'manual';

export interface UnmatchedHolding {
  id: string;
  symbol: string;
  providerCompanyName: string | null;
}

export interface Link {
  brokerageName: string;
  unmatchedTickers: string[];
  unmatchedHoldings: UnmatchedHolding[];
  status: PortfolioLinkStatus;
}

export interface Analysis {
  id: string;
  roi: number;
  roiValue: number | null;
  value: number;
  return7d: number | null;
  return7dValue: number | null;
  return3m: number | null;
  return3mValue: number | null;
  capitalInvested: number | null;
}

export interface PortfolioAnalysis extends Analysis {
  return7d: number | null;
  return1y: number | null;
  marketCountryISO: string;
  estimatedDividendYield: number;
  estimatedYearlyDividend: number | null;
  risksCount: number | null;
  rewardsCount: number | null;
}

export interface HoldingAnalysis extends Analysis {
  allocation: number;
  fairValue: number | null;
  return1y: number | null;
  return1yValue: number | null;
  valueUSD: number | null;
}

export interface HoldingFragmentType {
  id: string;
  units: number;
  company: Company;
  analysis: HoldingAnalysis;
  currencyISO: string;
  averagePurchasePrice: number | null;
  totalCost: number | null;
  updatedAt: string;
  manuallyMatched: boolean;
  partiallyMatched: boolean;
  symbol: string;
  isAnalysisRestricted: boolean;
}

type Provider = {
  name: string;
};

type Snowflake = {
  description: string | null;
  scores: {
    value: number | null;
    future: number | null;
    past: number | null;
    health: number | null;
    dividend: number | null;
  };
};

export interface PortfolioFragmentType {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  type: PortfolioType;
  link: Link;
  provider: Provider;
  currencyISO: string;
  manualInputOption: 'average_purchase_price' | 'total_cost';
  analysis: PortfolioAnalysis;
  holdings: HoldingFragmentType[];
  snowflake: Snowflake;
  isAnalysisRestricted: boolean;
}

const HOLDING_FRAGMENT = `
  fragment holdingFields on Holding {
    id
    units
    currencyISO
    averagePurchasePrice
    totalCost
    updatedAt
    manuallyMatched
    partiallyMatched
    symbol
    isAnalysisRestricted
    analysis {
      value
      valueUSD
      roi
      roiValue
      fairValue
      allocation
      capitalInvested
      return7d
      return7dValue
      return3m
      return3mValue
      return1y
      return1yValue
    }
    company {
      id
      name
      canonicalURL
      exchangeCountryISO
      tickerSymbol
      exchangeSymbol
      uniqueSymbol
      info {
        url
      }
      data {
        currencyInfo
      }
      score {
        value
        future
        past
        health
        dividend
        sentence
        total
      }
      analysisDividend {
        dividendYield
      }
      analysisFuture {
        forwardPe1Y
        forwardPriceToSales1Y
        revenueGrowthAnnual
        netIncomeGrowthAnnual
      }
      analysisHealth {
        netIncome
      }
      analysisValue {
        pe
        pb
        priceToSales
        preferredRelativeMultiple
        lastSharePrice
        npvPerShare
        marketCapUSD
        intrinsicDiscount
        priceTarget
        priceTargetAnalystCount
      }
    }
  }
`;

const ROOT_PORTFOLIO_FRAGMENT = `
  fragment rootPortfolioFields on Portfolio {
    id
    name
    createdAt
    currencyISO
    manualInputOption
    updatedAt
    type
    isAnalysisRestricted
    provider {
      name
    }
    link {
      brokerageName
      status
      unmatchedTickers
      unmatchedHoldings {
        id
        symbol
        providerCompanyName
      }
    }
    analysis {
      id
      value
      roi
      roiValue
      return7d
      return1y
      marketCountryISO
      capitalInvested
      estimatedDividendYield
      estimatedYearlyDividend
      risksCount
      rewardsCount
    }
    snowflake {
      description
      scores {
        value
        future
        past
        health
        dividend
      }
    }
  }
`;

const NARRATIVE_FRAGMENT = `
  fragment narrativeFields on Holding {
    company {
      myValuation {
        id
        fairValue
        type
        narrative {
          id
          author {
            id
            displayName
            name
            imageUrl
          }
        }
      }
    }
  }
`;

export const PORTFOLIO_FRAGMENT = `
  fragment portfolioFields on Portfolio {
    ...rootPortfolioFields
    holdings {
      ...holdingFields
      ...narrativeFields
    }
  }
  ${ROOT_PORTFOLIO_FRAGMENT}
  ${HOLDING_FRAGMENT}
  ${NARRATIVE_FRAGMENT}
`;

export const PUBLIC_PORTFOLIO_FRAGMENT = `
  fragment portfolioFields on Portfolio {
    ...rootPortfolioFields
    holdings {
      ...holdingFields
    }
  }
  ${ROOT_PORTFOLIO_FRAGMENT}
  ${HOLDING_FRAGMENT}
`;
